import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

const CftcDataChart = ({ currencyPair }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!currencyPair) {
        setError("Currency pair is required.");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch("https://vidplay-api.deng-software.com/fx/cftcdata", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ currencyPair }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const result = await response.json();
        setData(result.data || []);
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currencyPair]);

  // Define consistent colors for each trader category
  const traderColors = {
    dealer: "#4CAF50", // Green
    assetMgr: "#FF9800", // Orange
    levMoney: "#3F51B5", // Blue
    otherRept: "#009688", // Teal
    nonRept: "#795548", // Brown
  };

  // Prepare data for Plotly charts
  const chartData = data.map((item) => ({
    date: item.reportDateAsMMDDYYYY,
    dealerLong: Number(item.dealerPositionsLongAll) || 0,
    assetMgrLong: Number(item.assetMgrPositionsLongAll) || 0,
    levMoneyLong: Number(item.levMoneyPositionsLongAll) || 0,
    otherReptLong: Number(item.otherReptPositionsLongAll) || 0,
    nonReptLong: Number(item.nonReptPositionsLongAll) || 0,
    dealerShort: Number(item.dealerPositionsShortAll) || 0,
    assetMgrShort: Number(item.assetMgrPositionsShortAll) || 0,
    levMoneyShort: Number(item.levMoneyPositionsShortAll) || 0,
    otherReptShort: Number(item.otherReptPositionsShortAll) || 0,
    nonReptShort: Number(item.nonReptPositionsShortAll) || 0,
  }));

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">CFTC Data for {currencyPair}</h2>

      {error && <p className="text-red-500">{error}</p>}
      {loading && <p>Loading data...</p>}

      {data.length > 0 && (
        <>
          {/* Long Positions Chart */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-2">Long Positions Over Time</h3>
            <Plot
              data={[
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.dealerLong), type: "scatter", mode: "lines", name: "Dealer Long", line: { color: traderColors.dealer } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.assetMgrLong), type: "scatter", mode: "lines", name: "Asset Mgr Long", line: { color: traderColors.assetMgr } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.levMoneyLong), type: "scatter", mode: "lines", name: "Leverage Money Long", line: { color: traderColors.levMoney } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.otherReptLong), type: "scatter", mode: "lines", name: "Other Reportables Long", line: { color: traderColors.otherRept } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.nonReptLong), type: "scatter", mode: "lines", name: "Non-Reportable Long", line: { color: traderColors.nonRept } },
              ]}
              layout={{
                title: "Long Positions",
                xaxis: { title: "Date" },
                yaxis: { title: "Contracts" },
                responsive: true,
                showlegend: true,
              }}
              useResizeHandler
              style={{ width: "100%", height: "400px" }}
            />
          </div>

          {/* Short Positions Chart */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-2">Short Positions Over Time</h3>
            <Plot
              data={[
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.dealerShort), type: "scatter", mode: "lines", name: "Dealer Short", line: { color: traderColors.dealer } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.assetMgrShort), type: "scatter", mode: "lines", name: "Asset Mgr Short", line: { color: traderColors.assetMgr } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.levMoneyShort), type: "scatter", mode: "lines", name: "Leverage Money Short", line: { color: traderColors.levMoney } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.otherReptShort), type: "scatter", mode: "lines", name: "Other Reportables Short", line: { color: traderColors.otherRept } },
                { x: chartData.map((d) => d.date), y: chartData.map((d) => d.nonReptShort), type: "scatter", mode: "lines", name: "Non-Reportable Short", line: { color: traderColors.nonRept } },
              ]}
              layout={{
                title: "Short Positions",
                xaxis: { title: "Date" },
                yaxis: { title: "Contracts" },
                responsive: true,
                showlegend: true,
              }}
              useResizeHandler
              style={{ width: "100%", height: "400px" }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CftcDataChart;
